import { useState } from "react";
import { useTerminology } from '@/hooks/useTerminology';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { format } from "date-fns";

interface HireEmployeesWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (eventData: any) => void;
}

const roleOptions = [
  { value: "junior", label: "Junior Engineer (£35-45K)", salaryRange: [35000, 45000] },
  { value: "mid", label: "Mid-level Engineer (£45-60K)", salaryRange: [45000, 60000] },
  { value: "senior", label: "Senior Engineer (£60-80K)", salaryRange: [60000, 80000] },
  { value: "lead", label: "Lead Engineer (£80-100K)", salaryRange: [80000, 100000] },
];

export function HireEmployeesWizard({ open, onOpenChange, onComplete }: HireEmployeesWizardProps) {
  const { getCashMovementTerms, getScenarioTerm } = useTerminology();
  const [step, setStep] = useState(1);
  
  // Step 1 - Basic Information
  const [numEmployees, setNumEmployees] = useState(5);
  const [role, setRole] = useState("senior");
  const [salary, setSalary] = useState(60000);
  const [startDate, setStartDate] = useState<Date>(new Date(2026, 1, 1)); // Feb 1, 2026

  // Step 2 - Associated Costs
  const [includeSalaries, setIncludeSalaries] = useState(true);
  const [includeNI, setIncludeNI] = useState(true);
  const [includeRecruitment, setIncludeRecruitment] = useState(true);
  const [recruitmentTiming, setRecruitmentTiming] = useState("one-month-before");
  const [recruitmentPerPerson, setRecruitmentPerPerson] = useState(3000);
  const [includeEquipment, setIncludeEquipment] = useState(true);
  const [equipmentTiming, setEquipmentTiming] = useState("on-start");
  const [equipmentPerPerson, setEquipmentPerPerson] = useState(5000);
  const [includeOfficeSpace, setIncludeOfficeSpace] = useState(true);
  const [officeSpaceTiming, setOfficeSpaceTiming] = useState("one-month-after");
  const [officeSpacePerPerson, setOfficeSpacePerPerson] = useState(400);
  const [includeTraining, setIncludeTraining] = useState(false);
  const [trainingPerPerson, setTrainingPerPerson] = useState(0);

  // Step 3 - Revenue Impact
  const [revenueImpact, setRevenueImpact] = useState<"none" | "yes">("yes");
  const [revenuePerPerson, setRevenuePerPerson] = useState(10000);
  const [rampUpMonths, setRampUpMonths] = useState(3);

  // Step 4 - Review
  const [eventDescription, setEventDescription] = useState("");

  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleComplete = () => {
    const eventData = {
      type: "hire-employees",
      numEmployees,
      role,
      salary,
      startDate,
      costs: {
        salaries: includeSalaries,
        ni: includeNI,
        recruitment: includeRecruitment ? { amount: recruitmentPerPerson * numEmployees, timing: recruitmentTiming } : null,
        equipment: includeEquipment ? { amount: equipmentPerPerson * numEmployees, timing: equipmentTiming } : null,
        officeSpace: includeOfficeSpace ? { amount: officeSpacePerPerson * numEmployees, timing: officeSpaceTiming } : null,
        training: includeTraining ? { amount: trainingPerPerson * numEmployees } : null,
      },
      revenue: revenueImpact === "yes" ? { perPerson: revenuePerPerson, rampUpMonths } : null,
      description: eventDescription,
    };
    onComplete(eventData);
    onOpenChange(false);
    setStep(1);
  };

  const handleCancel = () => {
    onOpenChange(false);
    setStep(1);
  };

  // Calculations
  const totalSalaryPerYear = salary * numEmployees;
  const totalSalaryPerMonth = totalSalaryPerYear / 12;
  const niRate = 0.138;
  const niPerYear = totalSalaryPerYear * niRate;
  const niPerMonth = niPerYear / 12;
  const totalRecruitment = recruitmentPerPerson * numEmployees;
  const totalEquipment = equipmentPerPerson * numEmployees;
  const totalOfficeSpace = officeSpacePerPerson * numEmployees;
  const totalRevenuePerMonth = revenuePerPerson * numEmployees;

  // Calculate dates
  const getRecruitmentDate = () => {
    const date = new Date(startDate);
    if (recruitmentTiming === "one-month-before") {
      date.setMonth(date.getMonth() - 1);
    }
    return date;
  };

  const getEquipmentDate = () => {
    const date = new Date(startDate);
    if (equipmentTiming === "one-month-before") {
      date.setMonth(date.getMonth() - 1);
    }
    return date;
  };

  const getOfficeSpaceDate = () => {
    const date = new Date(startDate);
    if (officeSpaceTiming === "one-month-after") {
      date.setMonth(date.getMonth() + 1);
    } else if (officeSpaceTiming === "two-months-after") {
      date.setMonth(date.getMonth() + 2);
    }
    return date;
  };

  const getRevenueStartDate = () => {
    const date = new Date(startDate);
    date.setMonth(date.getMonth() + rampUpMonths);
    return date;
  };

  // Calculate 12-month impact
  const oneTimeCosts = 
    (includeRecruitment ? totalRecruitment : 0) +
    (includeEquipment ? totalEquipment : 0) +
    (includeTraining ? trainingPerPerson * numEmployees : 0);
  
  const monthlyRecurring = 
    (includeSalaries ? totalSalaryPerMonth : 0) +
    (includeNI ? niPerMonth : 0) +
    (includeOfficeSpace ? totalOfficeSpace : 0);
  
  const totalCost12Months = oneTimeCosts + (monthlyRecurring * 12);
  const revenueMonths = Math.max(0, 12 - rampUpMonths);
  const totalRevenue12Months = revenueImpact === "yes" ? totalRevenuePerMonth * revenueMonths : 0;
  const netImpact12Months = totalRevenue12Months - totalCost12Months;
  
  const breakEvenMonth = revenueImpact === "yes" && totalRevenuePerMonth > monthlyRecurring
    ? Math.ceil((oneTimeCosts + (monthlyRecurring * rampUpMonths)) / (totalRevenuePerMonth - monthlyRecurring)) + rampUpMonths
    : null;

  const minCashImpact = oneTimeCosts + (monthlyRecurring * rampUpMonths);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Hire Employees Event - Step {step} of 4</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="numEmployees">Number of employees *</Label>
                  <Input
                    id="numEmployees"
                    type="number"
                    value={numEmployees}
                    onChange={(e) => setNumEmployees(parseInt(e.target.value) || 0)}
                    min={1}
                  />
                </div>

                <div>
                  <Label htmlFor="role">Role/Level *</Label>
                  <Select value={role} onValueChange={(value) => {
                    setRole(value);
                    const selectedRole = roleOptions.find(r => r.value === value);
                    if (selectedRole) {
                      setSalary(Math.round((selectedRole.salaryRange[0] + selectedRole.salaryRange[1]) / 2));
                    }
                  }}>
                    <SelectTrigger id="role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="salary">Average salary per person *</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">£</span>
                    <Input
                      id="salary"
                      type="number"
                      value={salary}
                      onChange={(e) => setSalary(parseInt(e.target.value) || 0)}
                      min={0}
                    />
                    <span className="text-sm text-muted-foreground">per year</span>
                  </div>
                </div>

                <div>
                  <Label>Start date *</Label>
                  <DatePicker
                    value={startDate}
                    onChange={(date) => date && setStartDate(date)}
                    dateFormat="PPP"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleCancel}>Cancel</Button>
              <Button onClick={handleNext}>Next: Costs →</Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Select costs to include:</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-3 p-4 border rounded-lg">
                  <Checkbox
                    id="salaries"
                    checked={includeSalaries}
                    onCheckedChange={(checked) => setIncludeSalaries(checked as boolean)}
                  />
                  <div className="flex-1">
                    <Label htmlFor="salaries" className="font-semibold cursor-pointer">Salaries</Label>
                    <p className="text-sm text-muted-foreground">
                      £{totalSalaryPerYear.toLocaleString()}/year (£{Math.round(totalSalaryPerMonth).toLocaleString()}/month)
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Starting: {format(startDate, "MMM d, yyyy")}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 border rounded-lg">
                  <Checkbox
                    id="ni"
                    checked={includeNI}
                    onCheckedChange={(checked) => setIncludeNI(checked as boolean)}
                  />
                  <div className="flex-1">
                    <Label htmlFor="ni" className="font-semibold cursor-pointer">Employer National Insurance (13.8%)</Label>
                    <p className="text-sm text-muted-foreground">
                      £{Math.round(niPerYear).toLocaleString()}/year (£{Math.round(niPerMonth).toLocaleString()}/month)
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Starting: {format(startDate, "MMM d, yyyy")}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 border rounded-lg">
                  <Checkbox
                    id="recruitment"
                    checked={includeRecruitment}
                    onCheckedChange={(checked) => setIncludeRecruitment(checked as boolean)}
                  />
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="recruitment" className="font-semibold cursor-pointer">Recruitment Fees</Label>
                    <p className="text-sm text-muted-foreground">
                      £{recruitmentPerPerson.toLocaleString()} per person = £{totalRecruitment.toLocaleString()}
                    </p>
                    {includeRecruitment && (
                      <>
                        <div className="flex items-center gap-2">
                          <Label className="text-sm">Timing:</Label>
                          <Select value={recruitmentTiming} onValueChange={setRecruitmentTiming}>
                            <SelectTrigger className="w-[200px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="one-month-before">One month before</SelectItem>
                              <SelectItem value="on-start">On start date</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Date: {format(getRecruitmentDate(), "MMM d, yyyy")}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 border rounded-lg">
                  <Checkbox
                    id="equipment"
                    checked={includeEquipment}
                    onCheckedChange={(checked) => setIncludeEquipment(checked as boolean)}
                  />
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="equipment" className="font-semibold cursor-pointer">Equipment & Setup</Label>
                    <p className="text-sm text-muted-foreground">
                      £{equipmentPerPerson.toLocaleString()} per person = £{totalEquipment.toLocaleString()}
                    </p>
                    {includeEquipment && (
                      <>
                        <div className="flex items-center gap-2">
                          <Label className="text-sm">Timing:</Label>
                          <Select value={equipmentTiming} onValueChange={setEquipmentTiming}>
                            <SelectTrigger className="w-[200px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="one-month-before">One month before</SelectItem>
                              <SelectItem value="on-start">On start date</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Date: {format(getEquipmentDate(), "MMM d, yyyy")}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 border rounded-lg">
                  <Checkbox
                    id="officeSpace"
                    checked={includeOfficeSpace}
                    onCheckedChange={(checked) => setIncludeOfficeSpace(checked as boolean)}
                  />
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="officeSpace" className="font-semibold cursor-pointer">Office Space Expansion</Label>
                    <p className="text-sm text-muted-foreground">
                      £{officeSpacePerPerson} per person/month = £{totalOfficeSpace.toLocaleString()}/month
                    </p>
                    {includeOfficeSpace && (
                      <>
                        <div className="flex items-center gap-2">
                          <Label className="text-sm">Starting:</Label>
                          <Select value={officeSpaceTiming} onValueChange={setOfficeSpaceTiming}>
                            <SelectTrigger className="w-[200px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="on-start">On start date</SelectItem>
                              <SelectItem value="one-month-after">1 month after hire</SelectItem>
                              <SelectItem value="two-months-after">2 months after hire</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Date: {format(getOfficeSpaceDate(), "MMM d, yyyy")}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 border rounded-lg">
                  <Checkbox
                    id="training"
                    checked={includeTraining}
                    onCheckedChange={(checked) => setIncludeTraining(checked as boolean)}
                  />
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="training" className="font-semibold cursor-pointer">Training Costs (Optional)</Label>
                    {includeTraining && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm">£</span>
                        <Input
                          type="number"
                          value={trainingPerPerson}
                          onChange={(e) => setTrainingPerPerson(parseInt(e.target.value) || 0)}
                          placeholder="0"
                          className="w-32"
                        />
                        <span className="text-sm text-muted-foreground">per person</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleBack}>← Back</Button>
              <Button onClick={handleNext}>Next: Revenue →</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Will these hires generate additional revenue?</h3>
              
              <RadioGroup value={revenueImpact} onValueChange={(value) => setRevenueImpact(value as "none" | "yes")}>
                <div className="flex items-center space-x-2 p-4 border rounded-lg">
                  <RadioGroupItem value="none" id="no-revenue" />
                  <div>
                    <Label htmlFor="no-revenue" className="font-semibold cursor-pointer">No revenue impact</Label>
                    <p className="text-sm text-muted-foreground">(Support/admin roles)</p>
                  </div>
                </div>

                <div className="flex items-start space-x-2 p-4 border rounded-lg">
                  <RadioGroupItem value="yes" id="yes-revenue" className="mt-1" />
                  <div className="flex-1 space-y-4">
                    <Label htmlFor="yes-revenue" className="font-semibold cursor-pointer">Yes, increases revenue</Label>
                    
                    {revenueImpact === "yes" && (
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Revenue per person per month:</Label>
                          <div className="flex items-center gap-2">
                            <span className="text-sm">£</span>
                            <Input
                              type="number"
                              value={revenuePerPerson}
                              onChange={(e) => setRevenuePerPerson(parseInt(e.target.value) || 0)}
                              className="w-32"
                            />
                          </div>
                        </div>

                        <div>
                          <Label>Ramp-up period (months until full productivity):</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              value={rampUpMonths}
                              onChange={(e) => setRampUpMonths(parseInt(e.target.value) || 0)}
                              className="w-32"
                              min={0}
                            />
                            <span className="text-sm text-muted-foreground">months</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            Revenue starts: {format(getRevenueStartDate(), "MMM d, yyyy")}
                          </p>
                        </div>

                        <div className="bg-muted p-4 rounded-lg space-y-1">
                          <p className="text-sm font-semibold">Ramp profile:</p>
                          {[...Array(rampUpMonths)].map((_, i) => {
                            const date = new Date(startDate);
                            date.setMonth(date.getMonth() + i);
                            return (
                              <p key={i} className="text-sm text-muted-foreground">
                                Month {i + 1} ({format(date, "MMM")}): 0% = £0
                              </p>
                            );
                          })}
                          <p className="text-sm text-muted-foreground">
                            Month {rampUpMonths + 1} ({format(getRevenueStartDate(), "MMM")}): 100% = £{totalRevenuePerMonth.toLocaleString()}/month
                          </p>
                        </div>

                        <Button variant="link" className="p-0 h-auto">Advanced: Custom ramp-up curve</Button>
                      </div>
                    )}
                  </div>
                </div>
              </RadioGroup>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleBack}>← Back</Button>
              <Button onClick={handleNext}>Next: Review →</Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Review Event Summary</h3>
              
              <div className="space-y-6">
                <div>
                  <p className="font-semibold">
                    Hiring: {numEmployees} {roleOptions.find(r => r.value === role)?.label.split(' (')[0]}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Starting: {format(startDate, "MMM d, yyyy")}
                  </p>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">CASH FLOW IMPACT</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold mb-2">
                        One-Time Costs ({format(getRecruitmentDate(), "MMM")}-{format(startDate, "MMM yyyy")}):
                      </p>
                      <div className="space-y-1 ml-4">
                        {includeRecruitment && (
                          <p className="text-sm text-muted-foreground">
                            • Recruitment: -£{totalRecruitment.toLocaleString()} ({format(getRecruitmentDate(), "MMM d")})
                          </p>
                        )}
                        {includeEquipment && (
                          <p className="text-sm text-muted-foreground">
                            • Equipment: -£{totalEquipment.toLocaleString()} ({format(getEquipmentDate(), "MMM d")})
                          </p>
                        )}
                        {includeTraining && trainingPerPerson > 0 && (
                          <p className="text-sm text-muted-foreground">
                            • Training: -£{(trainingPerPerson * numEmployees).toLocaleString()}
                          </p>
                        )}
                        <p className="text-sm font-semibold">
                          Total: -£{oneTimeCosts.toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-semibold mb-2">
                        Monthly Recurring ({format(startDate, "MMM yyyy")} onwards):
                      </p>
                      <div className="space-y-1 ml-4">
                        {includeSalaries && (
                          <p className="text-sm text-muted-foreground">
                            • Salaries: -£{Math.round(totalSalaryPerMonth).toLocaleString()}
                          </p>
                        )}
                        {includeNI && (
                          <p className="text-sm text-muted-foreground">
                            • Employer NI: -£{Math.round(niPerMonth).toLocaleString()}
                          </p>
                        )}
                        {includeOfficeSpace && (
                          <p className="text-sm text-muted-foreground">
                            • Office space: -£{totalOfficeSpace.toLocaleString()} (from {format(getOfficeSpaceDate(), "MMM")})
                          </p>
                        )}
                        <p className="text-sm font-semibold">
                          Monthly {getCashMovementTerms().out.toLowerCase()}: -£{Math.round(monthlyRecurring).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {revenueImpact === "yes" && (
                      <div>
                        <p className="text-sm font-semibold mb-2">
                          Revenue Impact ({format(getRevenueStartDate(), "MMM yyyy")} onwards):
                        </p>
                        <div className="space-y-1 ml-4">
                          <p className="text-sm text-muted-foreground">
                            • New revenue: +£{totalRevenuePerMonth.toLocaleString()}/month
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">NET IMPACT (12 months)</h4>
                  
                  <div className="space-y-2 ml-4">
                    <p className="text-sm text-muted-foreground">
                      Total cost: -£{Math.round(totalCost12Months).toLocaleString()}
                    </p>
                    {revenueImpact === "yes" && (
                      <p className="text-sm text-muted-foreground">
                        Total revenue: +£{Math.round(totalRevenue12Months).toLocaleString()} ({revenueMonths} months × £{totalRevenuePerMonth.toLocaleString()})
                      </p>
                    )}
                    <p className="text-sm font-semibold">
                      Net 12-month impact: {netImpact12Months >= 0 ? '+' : ''}£{Math.round(netImpact12Months).toLocaleString()}
                    </p>
                  </div>

                  {breakEvenMonth && (
                    <p className="text-sm text-muted-foreground mt-2 ml-4">
                      Break-even: Month {breakEvenMonth} ({format(new Date(startDate.getFullYear(), startDate.getMonth() + breakEvenMonth - 1), "MMM yyyy")})
                    </p>
                  )}

                  <div className="mt-4 ml-4">
                    <p className="text-sm font-semibold">
                      Min cash impact: -£{Math.round(minCashImpact).toLocaleString()} ({format(new Date(startDate.getFullYear(), startDate.getMonth() + rampUpMonths - 1), "MMMM yyyy")})
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Before new revenue kicks in
                    </p>
                  </div>
                </div>

                {minCashImpact > 100000 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-yellow-900">
                      ⚠️ Recommendation: Ensure £{Math.round(minCashImpact / 1000)}K cash buffer before proceeding
                    </p>
                  </div>
                )}

                <div>
                  <Label htmlFor="description">Event Description (optional):</Label>
                  <Textarea
                    id="description"
                    value={eventDescription}
                    onChange={(e) => setEventDescription(e.target.value)}
                    placeholder="Expand engineering team to support growth"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleBack}>← Back</Button>
              <Button onClick={handleComplete}>Add Event to {getScenarioTerm(true)}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
